import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, HostListener, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LeafletDirective, LeafletModule } from '@bluehalo/ngx-leaflet';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { TranslocoDatePipe, TranslocoLocaleService } from '@jsverse/transloco-locale';
import { Circle, control, divIcon, LatLng, latLng, LatLngExpression, Map, Marker, marker, MarkerClusterGroup, MarkerOptions, tileLayer, ZoomPanOptions } from 'leaflet';
import "leaflet-active-area";
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, debounceTime, distinctUntilChanged, fromEvent, interval, map, scan, startWith, Subject, Subscription, takeUntil, throwError, timer } from 'rxjs';
import { PublicBike } from '../../interfaces/bike';
import { CardGroup } from '../../interfaces/cardGroup.interface';
import { StationPublic } from '../../interfaces/station';
import { PublicTariffPlan } from '../../interfaces/tariffPlan.interface';
import { DurationPipe } from '../../pipes/duration.pipe';
import { ConfirmDialogsService } from '../../services/confirm-dialogs.service';
import { NotificationsService } from '../../services/notifications.service';
import { WebsocketService } from '../../services/websocket.service';
import { CenterControl } from './position-control';
import { DomSanitizer } from '@angular/platform-browser';
import { LongPressDirective } from '../../directives/long-press.directive';

interface BottomSheetConfig {
  snapPoints: number[];
  minHeight: number;
  animationDuration: number;
  elasticEffect: boolean;
}

interface StationMarkerOptions extends MarkerOptions {
  station: StationPublic;
}

export type LocationStatus = "IDLE" | "FETCHING" | "FETCHED" | "DENIED" | "UNAVAILABLE" | "ERROR" | "TIMEOUT"

const ARRIVAL_TERMINATE_TRESHOLD = 50

@Component({
  selector: 'app-home-view',
  standalone: true,
  imports: [
    LeafletModule,
    TooltipModule,
    RippleModule,
    CommonModule,
    TranslocoDirective,
    SkeletonModule,
    ButtonModule,
    DialogModule,
    FloatLabelModule,
    InputNumberModule,
    FormsModule,
    InputGroupModule,
    InputGroupAddonModule,
    MultiSelectModule,
    SelectButtonModule,
    InputTextModule,
    MessageModule,
    DurationPipe,
    SelectModule,
    TextareaModule,
    ProgressSpinnerModule,
    LongPressDirective
  ],
  providers: [TranslocoDatePipe],
  templateUrl: './home-view.component.html',
  styleUrl: './home-view.component.scss',
  animations: [
    trigger('cardGroupAnimation', [
      state('visible', style({
        transform: 'translateX(0%)',
        opacity: 1,
        visibility: 'visible',
      })),
      state('hidden-left', style({
        transform: 'translateX(-100%)',
        opacity: 0,
        visibility: 'hidden',
      })),
      state('hidden-right', style({
        transform: 'translateX(100%)',
        opacity: 0,
        visibility: 'hidden',
      })),
      transition('visible <=> hidden-left', animate('0.4s cubic-bezier(0.4, 0, 0.2, 1)')),
      transition('visible <=> hidden-right', animate('0.4s cubic-bezier(0.4, 0, 0.2, 1)')),
    ]),
    trigger('expandCollapse', [
      state('collapsed', style({
        height: '0',
        opacity: '0',
        overflow: 'hidden',
      })),
      state('expanded', style({
        height: '*',
        opacity: '1',
        overflow: 'visible',
      })),
      transition('collapsed <=> expanded', [
        animate('0.3s ease-in-out')
      ]),
    ]),
  ],
})
export class HomeViewComponent implements OnDestroy, OnInit {
  @ViewChild(LeafletDirective) leaflet!: LeafletDirective;
  curWindow = window

  destroy$ = new Subject<void>();
  isMobileView = window.innerWidth <= 768;

  userPosition?: LatLng;
  bikePosition?: LatLng;
  private lastPosition?: LatLng;
  private readonly LOCATION_UPDATE_THRESHOLD = 15;
  private readonly DEFAULT_ZOOM = 16;
  mapLayers: any = []
  private map?: Map;
  panLockUser: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  currentMapFocus: LatLngExpression = latLng(0, 0);

  userIcon = divIcon({
    className: 'location-marker',
    html: `
    <svg width="22" height="22" viewBox="0 0 22 22" fill="#4285F4" xmlns="http://www.w3.org/2000/svg">
      <circle cx="11" cy="11" r="9" stroke="#ffffff" stroke-width="3" />
    </svg>
  `,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

  userPositionMarker: Marker = new Marker(latLng(0, 0), { icon: this.userIcon, zIndexOffset: 1000 }).addEventListener('click', (e) => {
    e.originalEvent.preventDefault()
    e.originalEvent.stopPropagation()
    this.setLockUser(true)
  })

  bikeIcon = divIcon({
    className: 'location-marker bike',
    html: `
    <svg width="22" height="22" viewBox="0 0 22 22" fill="#f442dc" xmlns="http://www.w3.org/2000/svg">
      <circle cx="11" cy="11" r="9" stroke="#ffffff" stroke-width="3" />
    </svg>
  `,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

  bikePositionMarker: Marker = new Marker(latLng(0, 0), { icon: this.bikeIcon, zIndexOffset: 1000 }).addEventListener('click', (e) => {
    e.originalEvent.preventDefault()
    e.originalEvent.stopPropagation()
    this.setLockUser(true)
  })
  // currentPositionAccuracyCircle: Circle = new Circle(latLng(0, 0), { opacity: 0, radius: 0, fillColor: '#4285F4', fillOpacity: 0.05 });
  searchRadiusCircle: Circle = new Circle(latLng(0, 0), { opacity: 0, radius: 0, color: '#FF9800', fillOpacity: 0, dashArray: [8, 8], weight: 2, interactive: false });
  private watchId: number | undefined;
  private isFirstPosition = true;

  // Add these new properties to the component class
  currentSearchLocation?: LatLng; // Track where we're currently searching
  searchLocationMarker = new Marker(latLng(0, 0), {
    icon: divIcon({
      className: 'search-location-marker',
      html: `
      <svg width="22" height="22" viewBox="0 0 22 22" fill="#FF9800" xmlns="http://www.w3.org/2000/svg">
        <path d="M11 0C6.58 0 3 3.58 3 8c0 4.42 8 14 8 14s8-9.58 8-14c0-4.42-3.58-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z" fill="#FF9800"/>
      </svg>
    `,
      iconSize: [22, 22],
      iconAnchor: [11, 11]
    })
  });


  options = {
    layers: [
      tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}{r}.png', { maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>' })
    ],
    zoom: this.DEFAULT_ZOOM,
    center: this.currentMapFocus,
    zoomControl: false
  };
  positionControl = new CenterControl({ position: this.isMobileView ? 'topright' : 'bottomright' });
  zoomControl = control.zoom({
    position: this.isMobileView ? 'topright' : 'bottomright',
    zoomInText: '<span class="material-symbols-outlined">add</span>',
    zoomOutText: '<span class="material-symbols-outlined">remove</span>',
  })

  bikes: PublicBike[] = [];
  bikesLoading$ = new BehaviorSubject<boolean>(false)
  bikesLoadingUI$ = this.bikesLoading$.pipe(
    debounceTime(400)
  );
  bikesSub?: Subscription

  stations: StationPublic[] = []
  stationsLoading$ = new BehaviorSubject<boolean>(false)
  stationsLoadingUI$ = this.stationsLoading$.pipe(
    debounceTime(400)
  );
  stationMarkers: any = [];
  stationsClusterGroup = new MarkerClusterGroup();
  stationsLastReturnCode: number = 200;
  stationsSub?: Subscription


  selectedStation?: StationPublic;
  selectedStationLoading$ = new BehaviorSubject<boolean>(false)
  selectedStationLoadingUI$ = this.selectedStationLoading$.pipe(
    debounceTime(400)
  );
  selectedStationSub?: Subscription

  selectedBike?: PublicBike;
  selectedBikeLoading$ = new BehaviorSubject<boolean>(false)
  selectedBikeLoadingUI$ = this.selectedBikeLoading$.pipe(
    debounceTime(400)
  );
  selectedBikeSub?: Subscription

  selectedTariffPlan?: PublicTariffPlan;

  arrivalStations: StationPublic[] = []
  arrivalStationsLoading$ = new BehaviorSubject<boolean>(false)
  arrivalStationsLoadingUI$ = this.arrivalStationsLoading$.pipe(
    debounceTime(400)
  );
  arrivalStationsLastReturnCode: number = 200;
  arrivalStationsSub?: Subscription
  selectedArrivalStation?: StationPublic;

  changeArrivalStations: StationPublic[] = []
  changeArrivalStationsLoading$ = new BehaviorSubject<boolean>(false)
  changeArrivalStationsLoadingUI$ = this.changeArrivalStationsLoading$.pipe(
    debounceTime(400)
  );
  changeArrivalStationsLastReturnCode: number = 200;
  changeArrivalStationsSub?: Subscription
  selectedChangeArrivalStation?: StationPublic;


  private STATIONS_FILTERS_DEFAULT: {
    searchRadius: number,
    openStatus: 'open' | 'closed' | 'all',
    bikeAvailability: 'available' | 'notAvailable' | 'all',
  } = {
      searchRadius: 3000,
      openStatus: "all",
      bikeAvailability: "all",
    }

  tmpStationsFilters: {
    searchRadius: number,
    openStatus: 'open' | 'closed' | 'all',
    bikeAvailability: 'available' | 'notAvailable' | 'all',
  } = structuredClone(this.STATIONS_FILTERS_DEFAULT)

  stationsFilters: {
    searchRadius: number,
    openStatus: 'open' | 'closed' | 'all',
    bikeAvailability: 'available' | 'notAvailable' | 'all',
  } = structuredClone(this.STATIONS_FILTERS_DEFAULT)


  stationsFiltersDialogVisible: boolean = false

  private ARRIVAL_STATIONS_FILTERS_DEFAULT: {
    searchRadius: number,
    openStatus: 'open' | 'closed' | 'all',
    bikeAvailability: 'available' | 'notAvailable' | 'all',
  } = {
      searchRadius: 3000,
      openStatus: "all",
      bikeAvailability: "all",
    }

  tmpArrivalStationsFilters: {
    searchRadius: number,
    openStatus: 'open' | 'closed' | 'all',
    bikeAvailability: 'available' | 'notAvailable' | 'all',
  } = structuredClone(this.ARRIVAL_STATIONS_FILTERS_DEFAULT)

  arrivalStationsFilters: {
    searchRadius: number,
    openStatus: 'open' | 'closed' | 'all',
    bikeAvailability: 'available' | 'notAvailable' | 'all',
  } = structuredClone(this.ARRIVAL_STATIONS_FILTERS_DEFAULT)

  arrivalStationsFiltersDialogVisible: boolean = false


  private CHANGE_ARRIVAL_STATIONS_FILTERS_DEFAULT: {
    searchRadius: number,
    openStatus: 'open' | 'closed' | 'all',
    bikeAvailability: 'available' | 'notAvailable' | 'all',
  } = {
      searchRadius: 3000,
      openStatus: "all",
      bikeAvailability: "all",
    }

  tmpChangeArrivalStationsFilters: {
    searchRadius: number,
    openStatus: 'open' | 'closed' | 'all',
    bikeAvailability: 'available' | 'notAvailable' | 'all',
  } = structuredClone(this.ARRIVAL_STATIONS_FILTERS_DEFAULT)

  changeArrivalStationsFilters: {
    searchRadius: number,
    openStatus: 'open' | 'closed' | 'all',
    bikeAvailability: 'available' | 'notAvailable' | 'all',
  } = structuredClone(this.ARRIVAL_STATIONS_FILTERS_DEFAULT)


  changeArrivalStationsFiltersDialogVisible: boolean = false


  mapAddressSearchDialogVisible: boolean = false
  mapAddressSearchQuery: string = ""
  mapAddressSearchSubject = new Subject<string>();
  mapAddressSearchResults: any[] = []
  mapAddressSearchResultsLoading$ = new BehaviorSubject<boolean>(false)
  mapAddressSearchResultsLoadingUI$ = this.mapAddressSearchResultsLoading$.pipe(
    debounceTime(100)
  )
  mapAddressSearchSub?: Subscription

  bikeUnlockLoading$ = new BehaviorSubject<boolean>(false)
  bikeIdentifyLoading$ = new BehaviorSubject<boolean>(false)

  paymentLoading$ = new BehaviorSubject<boolean>(false)

  currentRideData: any
  elapsedTimeSub: Subscription = new Subscription()

  cardGroups: CardGroup[] = [
    {
      id: "blankLoading",
      visible: true,
      xOffset: 0,
      backAction: undefined,
    },
    {
      id: "stationsList",
      visible: false,
      xOffset: 50,
      onShow: () => {
        this.selectedStation = undefined;
        if (this.currentSearchLocation) {
          this.refreshStations(this.currentSearchLocation);
          this.flyTo(this.currentSearchLocation, this.DEFAULT_ZOOM);
          this.searchLocationMarker.setOpacity(1);
        } else if (this.userPosition) {
          this.setLockUser(true);
          this.refreshStations(this.userPosition);
        } else {
          this.stations = []
          this.refreshStationsMarkers(this.stations)
        }
        this.searchRadiusCircle.setStyle({ opacity: 0.4 })
      },
      onHide: () => {
        this.searchLocationMarker.setOpacity(0);
        this.searchRadiusCircle.setStyle({ opacity: 0 })
      },
      reportOptions: [
        { id: "searchIssues", label: "userReports.searchIssues" },
        { id: "dataViewIssues", label: "userReports.dataViewIssues" },
        { id: "other", label: "userReports.other" },
      ]
    },
    {
      id: "selectedStationWithBikes",
      visible: false,
      xOffset: 0,
      backAction: "stationsList",
      onShow: () => {
        if (this.selectedStation) {
          if (!this.currentSearchLocation) {
            this.setLockUser(false);
          }
          this.flyTo(latLng(this.selectedStation.lat, this.selectedStation.lng), 17);
          this.refreshSelectedStation(this.selectedStation.stationId);
          this.refreshBikes(this.selectedStation.stationId)
        }
      },
      onHide: () => {
        setTimeout(() => {
          this.openingTimesExpanded = false;
        }, 400);
      },
      reportOptions: [
        { id: "incorrectData", label: "userReports.incorrectData" },
        { id: "searchIssues", label: "userReports.searchIssues" },
        { id: "dataViewIssues", label: "userReports.dataViewIssues" },
        { id: "other", label: "userReports.other" },
      ]
    },
    {
      id: "selectedBike",
      visible: false,
      xOffset: 0,
      backAction: "selectedStationWithBikes",
      onShow: () => {
        if (this.selectedBike) {
          this.refreshSelectedBike(this.selectedBike.bikeId);
        }
        if (this.selectedStation) {
          if (!this.currentSearchLocation) {
            this.setLockUser(false);
          }
          this.flyTo(latLng(this.selectedStation.lat, this.selectedStation.lng), 17);
          this.refreshStationsMarkers([this.selectedStation])
        }
      },
      reportOptions: [
        { id: "incorrectData", label: "userReports.incorrectData" },
        { id: "dataViewIssues", label: "userReports.dataViewIssues" },
        { id: "other", label: "userReports.other" },
      ]
    },
    {
      id: "arrivalStationsList",
      visible: false,
      xOffset: 0,
      backAction: "selectedBike",
      onShow: () => {
        this.selectedArrivalStation = undefined;
        this.currentSearchLocation = undefined;
        if (this.userPosition) {
          this.setLockUser(true);
          this.refreshArrivalStations(this.userPosition);
        }
        this.searchRadiusCircle.setStyle({ opacity: 0.4 })
      },
      onHide: () => {
        this.currentSearchLocation = undefined;
        this.searchLocationMarker.setOpacity(0);
        this.searchRadiusCircle.setStyle({ opacity: 0 })
      },
      reportOptions: [
        { id: "searchIssues", label: "userReports.searchIssues" },
        { id: "dataViewIssues", label: "userReports.dataViewIssues" },
        { id: "other", label: "userReports.other" },
      ]
    },
    {
      id: "selectionResume",
      visible: false,
      xOffset: 0,
      backAction: "arrivalStationsList",
      onShow: () => {
        if (this.selectedStation && this.selectedArrivalStation) {
          this.setLockUser(false);
          this.refreshStationsMarkers([{ ...this.selectedStation, classOverride: "departure" }, { ...this.selectedArrivalStation, classOverride: "arrival" }])
          this.map?.fitBounds(this.stationsClusterGroup.getBounds(), { padding: [25, 25] })
        } else {
          window.location.reload();
        }
      },
      reportOptions: [
        { id: "incorrectData", label: "userReports.incorrectData" },
        { id: "dataViewIssues", label: "userReports.dataViewIssues" },
        { id: "other", label: "userReports.other" },
      ]
    },
    {
      id: "paymentMethods",
      visible: false,
      xOffset: 0,
      backAction: undefined,
      allowed: ["bikeUnlock"],
      onShow: () => {
        if (this.currentRideData.startStationDetails && this.currentRideData.endStationDetails) {
          this.setLockUser(false);
          this.refreshStationsMarkers([{ ...this.currentRideData.startStationDetails, classOverride: "departure" }, { ...this.currentRideData.endStationDetails, classOverride: "arrival" }])
          this.map?.fitBounds(this.stationsClusterGroup.getBounds(), { padding: [25, 25] })
          this.payExpTimerSet(this.currentRideData.reservation.creationTimestamp, this.currentRideData.reservation.expirationTimestamp);
        } else {
          window.location.reload();
        }
      },
      reportOptions: [
        { id: "paymentIssues", label: "userReports.paymentIssues" },
        { id: "other", label: "userReports.other" },
      ]
    },
    {
      id: "bikeUnlock",
      visible: false,
      xOffset: 0,
      backAction: undefined,
      allowed: ["ride"],
      onShow: () => {
        if (this.currentRideData) {
          this.setLockUser(false);
          this.refreshStationsMarkers([{ ...this.currentRideData.startStationDetails, classOverride: "departure" }])
          this.flyTo(latLng(this.currentRideData.startStationDetails.lat, this.currentRideData.startStationDetails.lng), 17);

          let resTimeout = this.currentRideData.tariffPlanDetails.reservationTimeout
          if (resTimeout) {
            let expiration = new Date(new Date(this.currentRideData.paymentTimestamp).getTime() + (resTimeout * 60 * 1000))
            this.unlockExpTimerSet(this.currentRideData.paymentTimestamp, expiration);
          }

        } else {
          window.location.reload();
        }
      },
      reportOptions: [
        { id: "incorrectData", label: "userReports.incorrectData" },
        { id: "unlockIssues", label: "userReports.unlockIssues" },
        { id: "other", label: "userReports.other" },
      ]
    },
    {
      id: "ride",
      visible: false,
      xOffset: 0,
      backAction: undefined,
      allowed: ["changeArrivalStationsList"],
      onShow: (from) => {
        if (this.currentRideData) {
          this.refreshStationsMarkers([{ ...this.currentRideData.startStationDetails, classOverride: "departure" }, { ...this.currentRideData.endStationDetails, classOverride: "arrival" }])

          this.setLockUser(true)

          if (from != "changeArrivalStationsList") {
            Object.values(this.wsSubs).forEach(s => s.unsubscribe());
            this.wsSubs["BIKE_INFO"] = this.wss.subscribe(this.currentRideData.imei, "BIKE_INFO").pipe(
              takeUntil(this.destroy$)
            ).subscribe({
              next: (v) => {
                if (v) {
                  v = v.data
                  this.initCurRideIotDetails()
                  this.currentRideData.iotDetails.bike.battery.status = v.charging_status
                  this.currentRideData.iotDetails.bike.battery.level = Number(v.vehicle_power_percentage)
                  this.currentRideData.iotDetails.bike.currentSpeed = Number(v.current_speed)
                  this.currentRideData.iotDetails.bike.remainingMileage = Number(v.estimated_remaining_mileage)
                  this.currentRideData.iotDetails.bike.rideMileage = Number(v.ride_mileage)
                  this.currentRideData.iotDetails.bike.faultCode = v.fault_code
                  this.currentRideData.iotDetails.bike.speedMode = v.vehicle_current_mode
                }
              }
            })

            this.wsSubs["UNLOCK"] = this.wss.subscribe(this.currentRideData.imei, "UNLOCK").pipe(
              takeUntil(this.destroy$)
            ).subscribe({
              next: (v) => {
                if (v) {
                  const fromCache = v.fromCache || false
                  if (!fromCache) {
                    v = v.data
                    this.initCurRideIotDetails()
                    this.currentRideData.iotDetails.lock.isLocked = v.lock_status === "1"

                    if (this.currentRideData.iotDetails.lock.isLocked) return;

                    if (this.lockActionDialog.visible) {
                      this.lockActionDialog.visible = false
                    }

                  }
                }
              }
            })

            this.wsSubs["LOCK"] = this.wss.subscribe(this.currentRideData.imei, "LOCK").pipe(
              takeUntil(this.destroy$)
            ).subscribe({
              next: (v) => {
                if (v) {
                  const fromCache = v.fromCache || false
                  if (!fromCache) {
                    v = v.data
                    this.initCurRideIotDetails()
                    this.currentRideData.iotDetails.lock.isLocked = v.lock_status === "1"

                    if (!this.currentRideData.iotDetails.lock.isLocked) return;
                    if (this.lockActionDialog.visible) {
                      switch (this.lockActionDialog.context) {
                        case "pause":
                          this.pauseRideClick()
                          break;

                        case "terminate":
                          this.terminateRideClick()
                          break
                      }
                      this.lockActionDialog.visible = false

                    } else {
                      this.notifications.showNotification({
                        severity: "info",
                        title: 'bikeHasBeenLocked',
                        detail: 'bikeHasBeenLockedMessage',
                      })
                    }
                  }
                }
              }
            })

            this.wsSubs["POSITION"] = this.wss.subscribe(this.currentRideData.imei, "POSITION").pipe(
              takeUntil(this.destroy$)
            ).subscribe({
              next: (v) => {
                if (v) {
                  v = v.data
                  if (!v.latitude || !v.longitude) return;
                  this.initCurRideIotDetails()
                  var prePos = this.bikePosition
                  this.bikePositionMarker.setOpacity(1);
                  this.userPositionMarker.setOpacity(0);
                  this.bikePosition = latLng(v.latitude, v.longitude);
                  this.bikePositionMarker.setLatLng(this.bikePosition);
                  if (this.panLockUser.getValue()) {
                    this.flyTo(this.bikePosition, prePos ? (this.map?.getZoom() ?? 19) : 19);
                  }
                  this.currentRideData.iotDetails.location = {
                    lat: Number(v.latitude),
                    lng: Number(v.longitude),
                    alt: Number(v.altitude),
                    accuracy: Number(v.accuracy),
                    numberOfSatellites: Number(v.satellites),
                    lastUpdate: new Date()
                  }

                  this.currentRideData.iotDetails.location.isCloseToArrival = (
                    this.calculateDistance(
                      this.currentRideData.iotDetails.location.lat,
                      this.currentRideData.iotDetails.location.lng,
                      this.currentRideData.endStationDetails.lat,
                      this.currentRideData.endStationDetails.lng)
                    < ARRIVAL_TERMINATE_TRESHOLD
                  )

                }
              }
            })

            this.wsSubs["HEARTBEAT"] = this.wss.subscribe(this.currentRideData.imei, "HEARTBEAT").pipe(
              takeUntil(this.destroy$)
            ).subscribe({
              next: (v) => {
                if (v) {
                  v = v.data
                  this.initCurRideIotDetails()
                  this.currentRideData.iotDetails.bike.battery.level = Number(v.bike_power_percentage)
                  this.currentRideData.iotDetails.iot.errorCode = v.error_code
                  this.currentRideData.iotDetails.iot.networkSignal = Number(v.network_signal_value)
                  this.currentRideData.iotDetails.lock = {
                    battery: {
                      level: Number(v.lock_power_percentage),
                      voltage: Number(v.lock_current_voltage)
                    },
                    isLocked: v.lock_status === "1",
                    lockChangeTimestamp: new Date()
                  }
                }
              }
            })
          }

          this.elapsedTimeSub.unsubscribe()
          if (this.currentRideData.unlockTimestamp) {
            const elapsedTimeInitial = Math.floor((Date.now() - new Date(this.currentRideData.unlockTimestamp).getTime()) / 1000)
            this.currentRideData.elapsedTimeSeconds = elapsedTimeInitial
            this.elapsedTimeSub = interval(1000).pipe(
              startWith(0),
              scan(acc => acc + 1, elapsedTimeInitial),
              takeUntil(this.destroy$)
            ).subscribe({
              next: (v) => {
                this.currentRideData.elapsedTimeSeconds = v
              }
            })
          } else {
            this.currentRideData.elapsedTimeSeconds = "--"
          }

        } else {
          window.location.reload();
        }
      },
      onHide: (to) => {
        if (to != "changeArrivalStationsList") {
          Object.values(this.wsSubs).forEach(s => s.unsubscribe());
          this.bikePositionMarker.setOpacity(0);
          if (this.userPosition && !this.userPosition.equals(latLng(0, 0))) {
            this.userPositionMarker.setOpacity(1);
            this.centerOnUser()
          }
          this.bikePosition = undefined
        }
        this.elapsedTimeSub.unsubscribe()

      },
      reportOptions: [
        { id: "incorrectData", label: "userReports.incorrectData" },
        { id: "dataViewIssues", label: "userReports.dataViewIssues" },
        { id: "unlockIssues", label: "userReports.unlockIssues" },
        { id: "lockIssues", label: "userReports.lockIssues" },
        { id: "localizationIssues", label: "userReports.localizationIssues" },
        { id: "connectionIssues", label: "userReports.connectionIssues" },
        { id: "bikeIssues", label: "userReports.bikeIssues" },
        { id: "crashOrDamage", label: "userReports.crashOrDamage" },
        { id: "rideIssues", label: "userReports.rideIssues" },
        { id: "other", label: "userReports.other" },
      ]
    },
    {
      id: "changeArrivalStationsList",
      visible: false,
      xOffset: 0,
      backAction: "ride",
      onShow: () => {
        this.selectedChangeArrivalStation = undefined;
        this.currentSearchLocation = undefined;
        if (this.bikePosition) {
          this.setLockUser(false);
          this.centerOnUser()
          this.refreshChangeArrivalStations(this.bikePosition);
        }
        this.searchRadiusCircle.setStyle({ opacity: 0.4 })
      },
      onHide: () => {
        this.currentSearchLocation = undefined;
        this.searchLocationMarker.setOpacity(0);
        this.searchRadiusCircle.setStyle({ opacity: 0 })
      },
      reportOptions: [
        { id: "searchIssues", label: "userReports.searchIssues" },
        { id: "dataViewIssues", label: "userReports.dataViewIssues" },
        { id: "other", label: "userReports.other" },
      ]
    },
  ]

  activeGroupId?: string;
  setCardGroupVisibility(
    newGroupId: string,
    outTo: 'left' | 'right',
    inFrom: 'left' | 'right',
    forceAllowed: boolean = false
  ) {
    const activeGroup = this.cardGroups.find(c => c.visible);
    if (activeGroup) {
      if (!forceAllowed && activeGroup.allowed && !activeGroup.allowed.includes(newGroupId)) {
        return;
      }
      activeGroup.visible = false;
      activeGroup.xOffset = outTo === 'left' ? -100 : 100;
      if (activeGroup.onHide) {
        // setTimeout(() => {
        activeGroup.onHide(newGroupId);
        // }, 300);
      }
    }

    const newGroup = this.cardGroups.find(c => c.id === newGroupId);
    if (newGroup) {
      // this.DRAG_AREA_HEIGHT = newGroup.dragAreaHeight;
      newGroup.xOffset = inFrom === 'left' ? -100 : 100;
      setTimeout(() => {
        newGroup.visible = true;
        const prevId = this.activeGroupId;
        this.activeGroupId = newGroupId;
        if (newGroup.onShow) {
          newGroup.onShow(prevId);
        }
      }, 1);
    }
  }

  getCardGroupState(groupId: string): string {
    const group = this.cardGroups.find(c => c.id === groupId);
    if (!group) return 'hidden-left';

    if (group.visible) {
      return 'visible';
    } else {
      return group.xOffset < 0 ? 'hidden-left' : 'hidden-right';
    }
  }

  onCardGroupBack() {
    const active = this.cardGroups.find(c => c.visible);
    if (active && active.backAction) {
      this.setCardGroupVisibility(active.backAction, 'right', 'left');
      if (active.onBack) {
        active.onBack();
      }
    }
  }


  private dataRefreshMinute?: Subscription;
  locationStatus$: BehaviorSubject<LocationStatus> = new BehaviorSubject<LocationStatus>("IDLE");
  locationStatus: LocationStatus = "IDLE";

  get isGpsAvailable() {
    return ["IDLE", "FETCHING", "FETCHED"].includes(this.locationStatus);
  }

  tariffPlanDialogVisible: boolean = false;


  payExpBarW: number = 0;
  payExpTimer?: Subscription;
  payExpRemaining: number = 0;

  unlockExpBarW: number = 0;
  unlockExpTimer?: Subscription;
  unlockExpRemaining: number = 0;

  wsSubs: { [id: string]: Subscription } = {}

  reportDialogVisible: boolean = false;
  currentReportData: {
    category?: string,
    details?: string
  } = {}
  currentReportCategoryOptions: any[] = []

  get isReportDetailsRequired() {
    return this.currentReportData.category && (this.currentReportCategoryOptions.find(o => o.id === this.currentReportData.category)?.requiresDetails ?? false);
  }

  connectionErrorDialogVisible: boolean = false;
  previousWsStatus?: string;

  lockActionDialog = {
    visible: false,
    title: '',
    message: '',
    context: ""
  }

  constructor(
    private zone: NgZone,
    private http: HttpClient,
    private transloco: TranslocoService,
    private translocoDate: TranslocoDatePipe,
    private localeService: TranslocoLocaleService,
    private notifications: NotificationsService,
    private confDialogs: ConfirmDialogsService,
    private wss: WebsocketService,
    private sanitizer: DomSanitizer,
    @Inject('ENVIRONMENT') private ENV: any
  ) {
    http.post(this.ENV.apiUrl + "getUserRide", {}).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        let data = resp.data;
        if (data) {
          if ("paymentForm" in data) {
            data.paymentForm = sanitizer.bypassSecurityTrustHtml(data.paymentForm)
          }
          this.currentRideData = data
          if (data.status == "WAITING_FOR_PAYMENT") {
            this.setCardGroupVisibility('paymentMethods', 'left', 'right');
          } else if (data.status == "WAITING_FOR_UNLOCK") {
            this.setCardGroupVisibility('bikeUnlock', 'left', 'right');
          } else if (data.status == "IN_PROGRESS" || data.status == "PAUSED") {
            this.setCardGroupVisibility('ride', 'left', 'right');
          }
        } else {
          this.setCardGroupVisibility('stationsList', 'left', 'right');
        }
      },
      error: () => {
        this.setCardGroupVisibility('stationsList', 'left', 'right');
      }
    })

    this.positionControl.setLockSubject(this.panLockUser);
    this.positionControl.setPositionAvailableSubject(this.locationStatus$.pipe(map(status => status === "FETCHING" || status === "IDLE" || status === "FETCHED")));

    if (this.dataRefreshMinute) {
      this.dataRefreshMinute.unsubscribe();
    }

    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000;

    this.dataRefreshMinute = timer(msUntilNextMinute, 60000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      switch (this.activeGroupId) {
        case 'stationsList':
          this.refreshStations();
          break;
        case 'arrivalStationsList':
          this.refreshArrivalStations();
          break;
        case 'selectedStationWithBikes':
          this.refreshSelectedStation(this.selectedStation?.stationId);
          this.refreshBikes(this.selectedStation?.stationId);
          break;
      }
    });

    this.locationStatus$.pipe(takeUntil(this.destroy$)).subscribe(status => {
      this.locationStatus = status;
    });

    this.mapAddressSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(query => {
      this.mapAddressSearchQuery = query;
      if (query.length > 2) {
        this.mapAddressSearchResultsLoading$.next(true);
        this.mapAddressSearchSub?.unsubscribe()
        this.mapAddressSearchSub = this.http.get("https://nominatim.openstreetmap.org/search?q=" + encodeURI(this.mapAddressSearchQuery) + "&format=json&limit=10&dedupe=1").subscribe({
          next: (resp: any) => {
            this.mapAddressSearchResults = resp
            this.mapAddressSearchResultsLoading$.next(false);
          },
          error: (err) => {
            this.mapAddressSearchResultsLoading$.next(false);
          }
        })
      } else {
        this.mapAddressSearchResults = []
      }
    });

    wss.getConnectionStatus().pipe(takeUntil(this.destroy$)).subscribe(status => {
      this.connectionErrorDialogVisible = status == "disconnected";

      if (status === "connected" && this.previousWsStatus === "disconnected") {
        notifications.showNotification({
          severity: "success",
          title: "connectionRestored"
        })
      }

      if (!this.previousWsStatus && status == "disconnected") {
        // Skip the first connection error
      } else {
        this.previousWsStatus = status
      }

    })
  }

  // ngAfterViewInit() {
  //   setTimeout(() => {
  //     if (this.map) {
  //       this.map.invalidateSize();
  //     }
  //   }, 100);
  // }


  onMapReady(map: Map) {
    this.map = map;
    this.userPositionMarker.addTo(map);
    this.bikePositionMarker.addTo(map);
    this.stationsClusterGroup.addTo(map);
    this.searchLocationMarker
      .setOpacity(0)
      .addTo(map);
    this.searchRadiusCircle.setRadius(this.stationsFilters.searchRadius).addTo(map);
    // this.currentPositionAccuracyCircle.addTo(map);

    if (this.activeGroupId == "ride") {
      this.userPositionMarker.setOpacity(0)
      this.bikePositionMarker.setOpacity(1)
    } else {
      this.bikePositionMarker.setOpacity(0)

      if (this.userPosition) {
        this.userPositionMarker.setOpacity(1);
      } else {
        this.userPositionMarker.setOpacity(0)
      }
    }

    this.zoomControl.addTo(map);
    this.positionControl.addTo(map);

    (map as any).centerOnUser = () => this.centerOnUser();
    (map as any).toggleLockUser = () => this.toggleLockUser();

    setTimeout(() => {
      (map as any).setActiveArea("activeArea", true, true);
      this.map?.invalidateSize();
      this.refreshActiveArea();
      this.setupLocationWatcher();
    }, 1);
  }

  onMapClick(e: any) {
    if (this.activeGroupId == "stationsList" || this.activeGroupId == "arrivalStationsList" || this.activeGroupId == "changeArrivalStationsList") {
      this.setSearchLocationMarker(latLng(e.latlng));
    }
  }

  setSearchLocationMarker(position: LatLng) {
    this.currentSearchLocation = position;
    this.searchLocationMarker
      .setLatLng(position)
      .setOpacity(1);
    this.searchRadiusCircle.setLatLng(position);
    this.panLockUser.next(false);
    this.flyTo(position, (this.map?.getZoom() ?? 0) < 10 ? this.DEFAULT_ZOOM : undefined);
    if (this.activeGroupId == "stationsList") {
      this.refreshStations(position);
    } else if (this.activeGroupId == "arrivalStationsList") {
      this.refreshArrivalStations(position);
    } else if (this.activeGroupId == "changeArrivalStationsList") {
      this.refreshChangeArrivalStations(position);
    }
  }

  private checkPermissionState() {
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' }).then(permissionStatus => {
        // Listen for changes in permission state
        permissionStatus.onchange = () => {
          if (permissionStatus.state === 'granted') {
            this.locationStatus$.next("FETCHED");
            this.isFirstPosition = true
            this.fetchLocation(); // Fetch location immediately when permission is granted
          } else if (permissionStatus.state === 'denied') {
            this.locationStatus$.next("DENIED");
          }
        };

        // Initial state check
        if (permissionStatus.state === 'granted') {
          this.locationStatus$.next("FETCHED");
        } else if (permissionStatus.state === 'denied') {
          this.locationStatus$.next("DENIED");
        }
      }).catch(error => {
        console.error('Error checking permission state:', error);
      });
    }
  }

  private setupLocationWatcher() {
    if (!navigator.geolocation) {
      this.locationStatus$.next("UNAVAILABLE");
      return;
    }

    this.locationStatus$.next("FETCHING");

    // Check permission state
    this.checkPermissionState();

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 30000
    };

    // Initial position
    this.fetchLocation()

    if (this.watchId) {
      navigator.geolocation.clearWatch(this.watchId);
    }

    // Watch position
    this.watchId = navigator.geolocation.watchPosition(
      (position) => this.locationUpdate(position),
      (error) => this.handleLocationError(error),
      options
    );
  }

  private fetchLocation() {
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 30000
    };
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.locationStatus$.next("FETCHED");
        this.locationUpdate(position, this.DEFAULT_ZOOM);
      },
      (error) => this.handleLocationError(error),
      options
    );
  }

  private handleLocationError(error: GeolocationPositionError) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        this.locationStatus$.next("DENIED");
        break;
      case error.POSITION_UNAVAILABLE:
        this.locationStatus$.next("UNAVAILABLE");
        break;
      case error.TIMEOUT:
        this.locationStatus$.next("TIMEOUT");
        break;
      default:
        this.locationStatus$.next("ERROR");
    }

    // Fallback to default position or other handling
    if (!this.userPosition) {
      this.setDefaultPosition();
    }
  }

  private setDefaultPosition() {
    this.userPosition = undefined
    this.userPositionMarker.setLatLng([0, 0]);
    this.userPositionMarker.setOpacity(0);
    // this.searchRadiusCircle.setRadius(0)

    // this.currentPositionAccuracyCircle.setLatLng(latLng(this.userLat, this.userLong));
    // this.currentPositionAccuracyCircle.setRadius(0);

    if (this.isFirstPosition) {
      this.flyTo([42.0320758, 11.9592973], 5);
    }
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.updateMobileView.bind(this));
    this.destroy$.next();
    this.destroy$.complete();
    if (this.map) {
      this.map.off();
    }
    if (this.watchId) {
      navigator.geolocation.clearWatch(this.watchId);
    }
  }

  centerOnUser() {
    this.currentSearchLocation = undefined;
    this.searchLocationMarker.setOpacity(0);
    if (this.activeGroupId == "stationsList") {
      if (this.userPosition) {
        this.flyTo(this.userPosition, this.DEFAULT_ZOOM);
        this.searchRadiusCircle.setLatLng(this.userPosition)
        this.refreshStations(this.userPosition);
      }
    } else if (this.activeGroupId == "arrivalStationsList") {
      if (this.userPosition) {
        this.flyTo(this.userPosition, this.DEFAULT_ZOOM);
        this.searchRadiusCircle.setLatLng(this.userPosition)
        this.refreshArrivalStations(this.userPosition);
      }
    } else if (this.activeGroupId == "ride") {
      if (this.bikePosition) {
        this.flyTo(this.bikePosition, 19);
      } else {
        this.flyTo(latLng(this.currentRideData.startStationDetails.lat, this.currentRideData.startStationDetails.lng), this.DEFAULT_ZOOM)
      }
    } else if (this.activeGroupId == "changeArrivalStationsList") {
      if (this.bikePosition) {
        this.flyTo(this.bikePosition, this.DEFAULT_ZOOM);
        this.searchRadiusCircle.setLatLng(this.bikePosition)
        this.refreshChangeArrivalStations(this.bikePosition);
      }
    }
  }

  toggleLockUser() {
    this.panLockUser.next(!this.panLockUser.getValue());
    if (this.panLockUser.getValue()) {
      this.centerOnUser();
    }
  }

  setLockUser(lock: boolean) {
    this.panLockUser.next(lock);
    if (lock) {
      this.centerOnUser();
    }
  }

  locationUpdate(position: GeolocationPosition, zoomLevel?: number) {
    const isInRideViews = this.activeGroupId == "ride" || this.activeGroupId == "changeArrivalStationsList"
    this.locationStatus$.next("FETCHED");
    const longitude = position.coords.longitude;
    const latitude = position.coords.latitude;
    this.userPosition = latLng(latitude, longitude);
    this.userPositionMarker.setLatLng(latLng(latitude, longitude));
    if (!this.userPosition || this.userPosition.equals(latLng(0, 0))) {
      this.userPositionMarker.setOpacity(0);
    } else {
      if (!isInRideViews) {
        this.userPositionMarker.setOpacity(1);
      }
    }
    if (!this.currentSearchLocation && !isInRideViews) {
      this.searchRadiusCircle.setLatLng(latLng(latitude, longitude));
    }
    // this.currentPositionAccuracyCircle.setLatLng(latLng(latitude, longitude));
    // this.currentPositionAccuracyCircle.setRadius(position.coords.accuracy);

    if (!this.lastPosition || this.calculateDistance(latitude, longitude, this.lastPosition.lat, this.lastPosition.lng) >= this.LOCATION_UPDATE_THRESHOLD) {
      this.lastPosition = latLng(latitude, longitude);

      if (!this.currentSearchLocation) {

        if (this.panLockUser.getValue() && !isInRideViews) {
          this.flyTo([latitude, longitude], zoomLevel ? zoomLevel : this.map?.getZoom());
        }

        if (this.activeGroupId == "stationsList") {
          this.refreshStations();
        } else if (this.activeGroupId == "arrivalStationsList") {
          this.refreshArrivalStations();
        }
      }
    }
  }

  getBatteryColor(batteryLevel?: number): string {
    if (!batteryLevel) {
      return "var(--p-gray-500)";
    }
    let res = undefined
    if (batteryLevel >= 80) {
      res = 'green-600';
    } else if (batteryLevel >= 60) {
      res = 'amber-600';
    } else if (batteryLevel >= 30) {
      res = 'orange-600';
    } else {
      res = 'red-600';
    }

    return "var(--p-" + res + ")";
  }

  stationClick(station: StationPublic) {
    if (this.activeGroupId == "stationsList") {
      if (station.stationId == this.selectedStation?.stationId || !this.cardGroupAllowed('selectedStationWithBikes')) return;
      this.selectedStation = station;
      this.setCardGroupVisibility('selectedStationWithBikes', 'left', 'right');
    } else if (this.activeGroupId == "arrivalStationsList") {
      if (station.stationId == this.selectedArrivalStation?.stationId || !this.cardGroupAllowed('selectionResume')) return;
      this.selectedArrivalStation = station;
      this.setCardGroupVisibility('selectionResume', 'left', 'right');
    } else if (this.activeGroupId == "changeArrivalStationsList") {
      this.changeArrivalStationClick(station);
    }

  }

  refreshStations(location?: LatLngExpression, hideFilters: boolean = false) {
    if (this.currentSearchLocation) {
      location = this.currentSearchLocation;
    }

    if (!location && this.userPosition) {
      location = this.userPosition;
    }


    if (!location || latLng(location).equals([0, 0])) return;
    let locationCoords = latLng(location);

    this.stationsLoading$.next(true);
    this.stationsSub?.unsubscribe()
    this.stationsSub = this.http.post(this.ENV.apiUrl + "getStationsPublic", { lat: locationCoords.lat, lng: locationCoords.lng, ...this.stationsFilters }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        this.stationsLastReturnCode = resp.code;
        this.stations = resp.data
        this.stationsLoading$.next(false);
        this.refreshStationsMarkers(this.stations)
        if (hideFilters) {
          this.hideStationsFilters();
        }
      },
      error: () => {
        this.stationsLoading$.next(false);
      }
    })
  }


  arrivalStationClick(station: StationPublic) {
    if (station.stationId == this.selectedArrivalStation?.stationId || !this.cardGroupAllowed('selectionResume')) return;

    const selectArrival = () => {
      this.selectedArrivalStation = station;
      this.setCardGroupVisibility('selectionResume', 'left', 'right');
    }

    if (!station.open) {
      this.confDialogs.showConfirmDialog({
        title: "stationCurrentlyClosed",
        message: "stationCurrentlyClosedMessage",
        messageParameters: { nextOpening: this.getNowOpenTimeSlot(station, true) },
        accept: () => { selectArrival() },
        acceptIcon: "pi pi-check",
        rejectIcon: "pi pi-times",
        icon: "pi pi-exclamation-triangle",
      })
    } else {
      selectArrival()
    }
  }

  refreshArrivalStations(location?: LatLngExpression, hideFilters: boolean = false) {
    if (this.currentSearchLocation) {
      location = this.currentSearchLocation;
    }

    if (!location && this.userPosition) {
      location = this.userPosition;
    }


    if (!location || latLng(location).equals([0, 0])) return;
    let locationCoords = latLng(location);

    this.arrivalStationsLoading$.next(true);
    this.arrivalStationsSub?.unsubscribe()
    this.arrivalStationsSub = this.http.post(this.ENV.apiUrl + "getArrivalStationsPublic", { lat: locationCoords.lat, lng: locationCoords.lng, ...this.arrivalStationsFilters }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        this.arrivalStationsLastReturnCode = resp.code;
        this.arrivalStations = resp.data
        this.arrivalStationsLoading$.next(false);
        this.refreshStationsMarkers(this.arrivalStations)
        if (hideFilters) {
          this.hideArrivalStationsFilters();
        }
      },
      error: () => {
        this.arrivalStationsLoading$.next(false);
      }
    })
  }

  changeArrivalStationClick(station: StationPublic) {
    if (station.stationId == this.selectedChangeArrivalStation?.stationId) return;

    const selectArrival = () => {
      this.selectedChangeArrivalStation = station;
      this.changeArrivalStationsLoading$.next(true);

      this.http.post(this.ENV.apiUrl + "changeArrivalStation", { newStation: this.selectedChangeArrivalStation.stationId }).pipe(takeUntil(this.destroy$)).subscribe({
        next: (resp: any) => {
          this.notifications.showNotification({
            severity: "success",
            title: "arrivalStationChanged",
          })
          this.changeArrivalStationsLoading$.next(false);
          this.currentRideData = resp.data;
          this.currentRideData.iotDetails.location.isCloseToArrival = (
            this.calculateDistance(
              this.currentRideData.iotDetails.location.lat,
              this.currentRideData.iotDetails.location.lng,
              this.currentRideData.endStationDetails.lat,
              this.currentRideData.endStationDetails.lng)
            < ARRIVAL_TERMINATE_TRESHOLD
          )
          this.setCardGroupVisibility('ride', 'right', 'left');
        },
        error: () => {
          this.changeArrivalStationsLoading$.next(false);
        }
      })
    }

    if (!station.open) {
      this.confDialogs.showConfirmDialog({
        title: "stationCurrentlyClosed",
        message: "stationCurrentlyClosedMessage",
        messageParameters: { nextOpening: this.getNowOpenTimeSlot(station, true) },
        accept: () => { selectArrival() },
        acceptIcon: "pi pi-check",
        rejectIcon: "pi pi-times",
        icon: "pi pi-exclamation-triangle",
      })
    } else {
      selectArrival()
    }
  }

  refreshChangeArrivalStations(location?: LatLngExpression, hideFilters: boolean = false) {
    if (this.currentSearchLocation) {
      location = this.currentSearchLocation;
    }

    if (!location && this.bikePosition) {
      location = this.bikePosition;
    }


    if (!location || latLng(location).equals([0, 0])) return;
    let locationCoords = latLng(location);

    this.changeArrivalStationsLoading$.next(true);
    this.changeArrivalStationsSub?.unsubscribe()
    this.changeArrivalStationsSub = this.http.post(this.ENV.apiUrl + "getArrivalStationsPublic", { lat: locationCoords.lat, lng: locationCoords.lng, exclude: this.currentRideData.endStation, ...this.changeArrivalStationsFilters }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        this.changeArrivalStationsLastReturnCode = resp.code;
        this.changeArrivalStations = resp.data
        this.changeArrivalStationsLoading$.next(false);
        this.refreshStationsMarkers(this.changeArrivalStations)
        if (hideFilters) {
          this.hideChangeArrivalStationsFilters();
        }
      },
      error: () => {
        this.changeArrivalStationsLoading$.next(false);
      }
    })
  }

  refreshStationsMarkers(stationsData: StationPublic[]) {
    this.stationsClusterGroup.clearLayers();
    this.stationMarkers.forEach((marker: any) => {
      marker.closePopup()
      marker.remove();
    });
    this.stationMarkers = [];

    stationsData.forEach(station => {
      var icon = "location_on"
      if (station.classOverride == "departure") {
        icon = "play_arrow"
      } else if (station.classOverride == "arrival") {
        icon = "sports_score"
      } else if (station.bonus) {
        icon = "percent"
      }
      const markerElement = marker(
        latLng(station.lat, station.lng),
        {
          station: station,
          icon: divIcon({
            className: 'station-marker-icon' + (this.isStationUnavailable(station) ? ' unavailable' : '') + ' ' + (station.bonus ? 'bonus' : '') + ' ' + (station.classOverride ?? ''),
            html: '<div class="marker-circle"><span class="material-symbols-outlined station-icon">' + (icon) + '</span></div>',
            iconSize: [40, 40],
            iconAnchor: [20, 20]
          })
        } as StationMarkerOptions
      );


      const popupContent = `
        <div>
          ${station.description}
        </div>
      `;

      // Add popup behavior
      markerElement.bindPopup(popupContent, {
        closeButton: false,
        className: 'station-popup',
        offset: [0, -4],
      });

      markerElement.on('mouseover', (e) => {
        markerElement.openPopup();
      });

      markerElement.on('mouseout', (e) => {
        markerElement.closePopup();
      });

      markerElement.on('click', (e) => {
        this.zone.run(() => {
          this.stationClick(station);
        });
      })


      this.stationsClusterGroup.addLayer(markerElement);
      this.stationMarkers.push(markerElement);
    });
  }

  refreshBikes(stationId?: string) {
    if (!stationId) return;
    this.bikesLoading$.next(true);
    this.bikesSub?.unsubscribe()
    this.bikesSub = this.http.post(this.ENV.apiUrl + "getBikesPublic", { stationId: stationId }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        this.bikes = resp.data
        this.bikesLoading$.next(false);
      },
      error: () => {
        this.bikesLoading$.next(false);
      }
    })
  }

  refreshSelectedStation(stationId?: string, location?: LatLngExpression) {
    if (!stationId) return;
    if (!location) location = this.userPosition;


    if (!location || latLng(location).equals([0, 0])) return;
    let locationCoords = latLng(location);

    this.selectedStationLoading$.next(true);
    this.selectedStationSub?.unsubscribe()
    this.selectedStationSub = this.http.post<any>(this.ENV.apiUrl + "getStationsPublic", { lat: locationCoords.lat, lng: locationCoords.lng, stationId: stationId }).pipe(
      map(resp => {
        let data = resp.data?.[0];
        if (!data) {
          throwError(() => new Error());
          return resp;
        }
        return resp.data[0];
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (resp: any) => {
        this.selectedStation = resp
        this.selectedStationLoading$.next(false);
      },
      error: () => {
        this.selectedStationLoading$.next(false);
        this.onCardGroupBack()
      }
    })
  }

  getStationAddressString(s?: StationPublic, truncate?: number) {
    if (!s) return "--";
    let result = (s.address ? s.address + ", " : "") + (s.city ? s.city + (s.zip ? " (" + s.zip + ")" : "") : "");
    if (result.length == 0) {
      result = "--"
    } else if (truncate && result.length > truncate) {
      result = result.substring(0, truncate) + "...";
    }
    return result;
  }

  isStationUnavailable(station: StationPublic): boolean {
    return station.bikesCount == 0 || (!station.open && !this.isOpeningSoon(station));
  }

  isArrivalStationUnavailable(station: StationPublic): boolean {
    return (!station.open && !this.isOpeningSoon(station));
  }

  getNowOpenTimeSlot(station: StationPublic, onlyTime: boolean = false) {
    if (station.open) {
      let times = station.openTime;
      if (times) {
        return this.translocoDate.transform(times.open, { timeStyle: 'short' }) + " - " + this.translocoDate.transform(times.close, { timeStyle: 'short' })
      } else {
        return ""
      }
    } else {
      if (station.nextOpen) {
        let nextOpenDate = new Date(station.nextOpen)
        let today = new Date();
        if (nextOpenDate.getDate() == today.getDate() && nextOpenDate.getMonth() == today.getMonth() && nextOpenDate.getFullYear() == today.getFullYear()) {
          if (onlyTime) {
            return this.transloco.translate("opensAtOnlyTime", {
              time: this.translocoDate.transform(station.nextOpen, { timeStyle: 'short' })
            })
          } else {
            return this.transloco.translate("opensAt", {
              time: this.translocoDate.transform(station.nextOpen, { timeStyle: 'short' })
            })
          }

        } else {
          if (onlyTime) {
            return this.transloco.translate("opensDayAtOnlyTime", {
              day: this.transloco.translate("primeng.dayNames")[nextOpenDate.getDay()],
              time: this.translocoDate.transform(station.nextOpen, { timeStyle: 'short' })
            })
          } else {
            return this.transloco.translate("opensDayAt", {
              day: this.transloco.translate("primeng.dayNamesShort")[nextOpenDate.getDay()],
              time: this.translocoDate.transform(station.nextOpen, { timeStyle: 'short' })
            })
          }

        }
      } else {
        return ""
      }
    }
  }

  isClosingSoon(station: StationPublic): boolean {
    if (!station.open || !station.nextClose) return false;
    let now = new Date();
    let closingTime = new Date(station.nextClose);
    return (closingTime.getTime() - now.getTime()) < 1000 * 60 * 15;
  }

  isOpeningSoon(station: StationPublic): boolean {
    if (station.open || !station.nextOpen) return false;
    let now = new Date();
    let openingTime = new Date(station.nextOpen);
    return (openingTime.getTime() - now.getTime()) < 1000 * 60 * 15;
  }

  getDayTimes(station: StationPublic, day: number) {
    let times = station.openingTimes?.[day]
    if (times) {
      let timesArray: string[] = []
      times.forEach(time => {
        timesArray.push(time.open.substring(0, 5) + " - " + time.close.substring(0, 5))
      })
      return "%t%" + timesArray.join("<br/>")
    } else {
      return "closed"
    }
  }

  openingTimesExpanded: boolean = false;

  bikeClick(bike: PublicBike) {
    if (!this.cardGroupAllowed('selectedBike')) return;
    this.selectedBike = bike;
    this.setCardGroupVisibility('selectedBike', 'left', 'right');
  }

  refreshSelectedBike(bikeId?: string) {
    if (!bikeId) return;
    this.selectedBikeLoading$.next(true);
    this.selectedBikeSub?.unsubscribe()
    this.selectedBikeSub = this.http.post(this.ENV.apiUrl + "getBikePublic", { bikeId: bikeId }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        this.selectedBike = resp.data
        this.selectedBikeLoading$.next(false);
      },
      error: () => {
        this.selectedBikeLoading$.next(false);
        this.onCardGroupBack()
      }
    })
  }


  planClick(plan: PublicTariffPlan) {
    if (!this.cardGroupAllowed('arrivalStationsList')) return;
    this.selectedTariffPlan = plan;
    this.setCardGroupVisibility('arrivalStationsList', 'left', 'right');
  }

  tariffPlanInfo?: PublicTariffPlan
  showTariffPlanInfo(plan: PublicTariffPlan) {
    this.tariffPlanDialogVisible = true;
    this.tariffPlanInfo = plan;
  }

  hideTariffPlanInfo() {
    this.tariffPlanDialogVisible = false;
  }

  payExpTimerSet(creationTimestamp: string | Date, expirationTimestamp: string | Date) {
    this.payExpTimer?.unsubscribe()
    this.payExpTimer = timer(0, 1000).subscribe(() => {
      const now = new Date();
      var expiresAt = new Date(expirationTimestamp);
      var createdAt = new Date(creationTimestamp);
      var totalDuration = (expiresAt.getTime() - createdAt.getTime()) / 1000;
      var elapsed = (now.getTime() - createdAt.getTime()) / 1000;
      this.payExpBarW = Math.max(0, parseFloat((100 - ((elapsed / totalDuration) * 100)).toFixed(1)));
      this.payExpRemaining = Math.max(0, Math.round(totalDuration - elapsed));
      if (now >= expiresAt) {
        this.payExpTimer?.unsubscribe()
        window.location.reload()
      }
    });
  }

  unlockExpTimerSet(creationTimestamp: string | Date, expirationTimestamp: string | Date) {
    this.unlockExpTimer?.unsubscribe()
    this.unlockExpTimer = timer(0, 1000).subscribe(() => {
      const now = new Date();
      var expiresAt = new Date(expirationTimestamp);
      var createdAt = new Date(creationTimestamp);
      var totalDuration = (expiresAt.getTime() - createdAt.getTime()) / 1000;
      var elapsed = (now.getTime() - createdAt.getTime()) / 1000;
      this.unlockExpBarW = Math.max(0, parseFloat((100 - ((elapsed / totalDuration) * 100)).toFixed(1)));
      this.unlockExpRemaining = Math.max(0, Math.round(totalDuration - elapsed));
      if (now >= expiresAt) {
        this.unlockExpTimer?.unsubscribe()
      }
    });
  }

  confirmRideClick() {
    if (!this.cardGroupAllowed('paymentMethods')) return;
    this.http.post(this.ENV.apiUrl + "checkAndConfirmRide", {
      departureStation: this.selectedStation?.stationId,
      arrivalStation: this.selectedArrivalStation?.stationId,
      bike: this.selectedBike?.bikeId,
      tariffPlan: this.selectedTariffPlan?.planId
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.currentRideData = resp.data
          this.setCardGroupVisibility('paymentMethods', 'left', 'right');
        },
        error: () => {

        }
      })
  }

  pauseRideClick() {
    if (!this.currentRideData) {
      return
    }

    if (this.currentRideData.iotDetails?.lock?.isLocked ?? false) {
      // La corsa viene messa in pausa dal backend in automatico
    } else {
      this.lockActionDialog = {
        visible: true,
        title: 'actionRequired',
        message: 'lockBikeToPauseRide',
        context: "pause"
      }
    }
  }

  terminateRideClick() {
    if (!this.currentRideData) {
      return
    }

    if (this.currentRideData.iotDetails?.lock?.isLocked ?? false) {
      this.bikeUnlockLoading$.next(true);
      this.http.post(this.ENV.apiUrl + "terminateRide", {})
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (resp: any) => {
            this.notifications.showNotification({
              severity: 'success',
              title: "rideTerminated",
              detail: "rideTerminatedDetail"
            })
            this.clearAll()
            this.bikeUnlockLoading$.next(false);
          },
          error: () => {
            this.bikeUnlockLoading$.next(false);
          }
        })
    } else {
      this.lockActionDialog = {
        visible: true,
        title: 'actionRequired',
        message: 'lockBikeToTerminateRide',
        context: "terminate"
      }
    }
  }

  cancelRideClick() {
    this.bikeUnlockLoading$.next(true);
    this.paymentLoading$.next(true);
    this.http.post(this.ENV.apiUrl + "cancelRide", {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.clearAll()
          this.bikeUnlockLoading$.next(false);
          this.paymentLoading$.next(false);
        },
        error: () => {
          // window.location.reload()
          this.bikeUnlockLoading$.next(false);
          this.paymentLoading$.next(false);
        }
      })
  }

  unlockBikeClick() {
    this.bikeUnlockLoading$.next(true);
    this.http.post(this.ENV.apiUrl + "unlockBikeAndResume", {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.bikeUnlockLoading$.next(false);
        },
        error: () => {
          this.bikeUnlockLoading$.next(false);
        }
      })
  }

  payButtonClick(method: string) {
    if (this.paymentLoading$.value) return

    this.paymentLoading$.next(true);

    this.http.post(this.ENV.apiUrl + "startPayment", {
      method: method
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          // this.currentRideData = resp.data;
          // this.setCardGroupVisibility('bikeUnlock', 'left', 'right');
          let redirectUrl = resp.data
          window.open(redirectUrl, "_SELF")
          this.paymentLoading$.next(false);

        },
        error: (err) => {
          if (err?.data == "reset") {
            this.clearAll()
          }
          if (err?.data == "reload") {
            window.location.reload()
          }
          this.paymentLoading$.next(false);

        }
      })
  }

  unlockBikeAndStartClick() {
    this.bikeUnlockLoading$.next(true);
    this.http.post(this.ENV.apiUrl + "unlockBikeAndStartRide", {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.currentRideData = resp.data;
          this.setCardGroupVisibility('ride', 'left', 'right');
          this.bikeUnlockLoading$.next(false);
        },
        error: () => {
          this.bikeUnlockLoading$.next(false);
        }
      })
  }

  identifyBikeClick() {
    this.bikeIdentifyLoading$.next(true);
    this.http.post(this.ENV.apiUrl + "identifyBike", {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.bikeIdentifyLoading$.next(false);
        },
        error: () => {
          this.bikeIdentifyLoading$.next(false);
        }
      })
  }

  changeArrivalStationBtnClick() {
    this.setCardGroupVisibility('changeArrivalStationsList', 'left', 'right');
  }


  clearAll() {
    this.selectedStation = undefined;
    this.selectedBike = undefined;
    this.selectedArrivalStation = undefined;
    this.selectedTariffPlan = undefined;
    this.currentSearchLocation = undefined;
    this.stationsFilters = structuredClone(this.STATIONS_FILTERS_DEFAULT);
    this.arrivalStationsFilters = structuredClone(this.ARRIVAL_STATIONS_FILTERS_DEFAULT);
    this.bikePosition = undefined;
    this.setCardGroupVisibility('stationsList', 'right', 'left', true);
  }

  showReportDialog() {
    this.currentReportCategoryOptions = (this.cardGroups.find(cg => cg.id === this.activeGroupId)?.reportOptions ?? []).map(e => {
      return {
        ...e,
        label: this.transloco.translate(e.label)
      }
    })
    this.reportDialogVisible = true;
  }

  hideReportDialog() {
    this.reportDialogVisible = false;
    this.currentReportData = {}
  }

  submitReportButtonClick() {
    this.http.post(this.ENV.apiUrl + "submitUserReport", {
      ...this.currentReportData,
      section: this.activeGroupId,
      context: JSON.stringify({
        startStationId: this.selectedStation?.stationId,
        startStationFilters: this.stationsFilters,
        endStationId: this.selectedArrivalStation?.stationId,
        endStationFilters: this.arrivalStationsFilters,
        bikeId: this.selectedBike?.bikeId,
        tariffPlanId: this.selectedTariffPlan?.planId,
        rideData: this.currentRideData,
        userPosition: this.userPosition,
        bikePosition: this.bikePosition
      })
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.hideReportDialog()
          this.notifications.showNotification({
            severity: 'success',
            title: "reportSubmitted",
            detail: "reportSubmittedMessage"
          })
        },
        error: () => {
        }
      })
  }


  ///////////////////////////////////////////
  // BOTTOM SHEET
  ///////////////////////////////////////////

  private updateMobileView() {
    this.isMobileView = window.innerWidth <= 768;

    if (this.isMobileView) {
      this.sheetTransformYVal = this.defaultMobileSnapPoint;
      this.sheetTransformY = `${this.defaultMobileSnapPoint}%`;
    } else {
      this.sheetTransformYVal = 0;
      this.sheetTransformY = '0%';
    }

    if (!this.map) return;

    // Remove existing controls
    this.map.removeControl(this.zoomControl);
    this.map.removeControl(this.positionControl);

    // Reinitialize controls based on the current isMobileView state
    this.zoomControl = control.zoom({
      position: this.isMobileView ? 'topright' : 'bottomright',
      zoomInText: '<span class="material-symbols-outlined">add</span>',
      zoomOutText: '<span class="material-symbols-outlined">remove</span>',
    });

    this.positionControl = new CenterControl({ position: this.isMobileView ? 'topright' : 'bottomright' });
    this.positionControl.setLockSubject(this.panLockUser);

    // Add the controls back to the map
    this.zoomControl.addTo(this.map);
    this.positionControl.addTo(this.map);
    this.refreshActiveArea()
  }

  bottomSheetConfig: BottomSheetConfig = {
    snapPoints: [10, 45, 90],
    minHeight: 150,
    animationDuration: 300,
    elasticEffect: true,
  };

  private defaultMobileSnapPoint = 45;
  sheetTransformY = '45%';
  sheetTransformYVal = 45;
  isDragging = false;
  startDragY = 0;
  startSheetY = 0;
  private startDragX = 0;
  private isHorizontalGesture = false
  private minHorizontalSwipeDistance = 100;
  private maxHorizontalSwipeTime = 300;
  private swipeStartTime = 0;
  private dominantSwipeDirection: 'horizontal' | 'vertical' | null = null;
  private minSwipeDistance = 10;


  ngOnInit() {
    this.adjustSnapPositions();
    this.updateMobileView();
    fromEvent(window, 'resize').pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(() => this.updateMobileView());
  }


  private adjustSnapPositions(): number[] {
    const windowHeight = window.innerHeight;
    const minVisiblePercent = (this.bottomSheetConfig.minHeight / windowHeight) * 100;
    return this.bottomSheetConfig.snapPoints.map((pos) => Math.min(pos, 100 - minVisiblePercent));
  }


  private isNestedScrollAtTop(element: HTMLElement): boolean {
    return element.scrollTop === 0;
  }

  private isNestedScrollAtBottom(element: HTMLElement): boolean {
    return element.scrollTop + element.clientHeight >= element.scrollHeight;
  }


  onDragStart(event: TouchEvent | MouseEvent): void {
    if (!this.isMobileView) return;

    event.stopPropagation();

    const target = event.target as HTMLElement;
    const nestedScrollElement = target.closest('.list-container') as HTMLElement;

    if (nestedScrollElement) {
      const isAtTop = this.isNestedScrollAtTop(nestedScrollElement);
      const isAtBottom = this.isNestedScrollAtBottom(nestedScrollElement);

      if (!isAtTop && !isAtBottom) return;
    }

    this.isDragging = true;
    this.startDragY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    this.startDragX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    this.startSheetY = this.sheetTransformYVal;
    this.isHorizontalGesture = false;
    this.swipeStartTime = Date.now();
    this.dominantSwipeDirection = null;
  }


  onDragMove(event: TouchEvent | MouseEvent): void {
    if (!this.isMobileView || !this.isDragging) return;

    event.stopPropagation();

    const target = event.target as HTMLElement;
    const nestedScrollElement = target.closest('.list-container') as HTMLElement;

    if (nestedScrollElement) {
      const isAtTop = this.isNestedScrollAtTop(nestedScrollElement);
      const isAtBottom = this.isNestedScrollAtBottom(nestedScrollElement);
      if (!isAtTop && !isAtBottom) {
        this.startDragY = 'touches' in event ? event.touches[0].clientY : event.clientY;
        this.startSheetY = this.sheetTransformYVal;
        return;
      }
    }

    const currentY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    const currentX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const deltaY = currentY - this.startDragY;
    const deltaX = currentX - this.startDragX;

    if (!this.dominantSwipeDirection) {
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) >= this.minSwipeDistance) {
        this.dominantSwipeDirection = 'horizontal';
      } else if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) >= this.minSwipeDistance) {
        this.dominantSwipeDirection = 'vertical';
      }
    }

    if (this.dominantSwipeDirection === 'vertical') {
      const maxTransform = 100 - (this.bottomSheetConfig.minHeight / window.innerHeight) * 100;
      let newPosition = this.startSheetY + (deltaY / window.innerHeight) * 100;

      if (this.bottomSheetConfig.elasticEffect) {
        if (newPosition > maxTransform) {
          const excess = newPosition - maxTransform;
          newPosition = maxTransform + excess * 0.2;
        } else if (newPosition < 0) {
          newPosition = newPosition * 0.2;
        }
      }

      if (newPosition <= 7) {
        newPosition = 7;
      }
      this.sheetTransformYVal = newPosition;
      this.sheetTransformY = `${newPosition}%`;
    }
  }

  onDragEnd(event: TouchEvent | MouseEvent): void {
    if (!this.isMobileView) return;

    event.stopPropagation();

    this.isDragging = false;

    // Check for horizontal swipe
    if (this.dominantSwipeDirection === 'horizontal') {
      const currentX = 'touches' in event ? event.changedTouches[0].clientX : event.clientX;
      const deltaX = currentX - this.startDragX;
      const swipeTime = Date.now() - this.swipeStartTime;

      if (Math.abs(deltaX) >= this.minHorizontalSwipeDistance &&
        swipeTime <= this.maxHorizontalSwipeTime) {
        this.onCardGroupBack();
        return;
      }
    }

    // Handle vertical drag as before
    const adjustedPositions = this.adjustSnapPositions();
    const targetPosition = adjustedPositions.reduce((prev, curr) =>
      Math.abs(curr - this.sheetTransformYVal) < Math.abs(prev - this.sheetTransformYVal) ? curr : prev
    );

    this.sheetTransformYVal = targetPosition;
    this.sheetTransformY = `${targetPosition}%`;

    this.refreshActiveArea();
  }

  @HostListener('window:mouseup', ['$event'])
  onWindowMouseUp(event: MouseEvent): void {
    if (!this.isMobileView) return;
    if (this.isDragging) {
      this.isDragging = false;
      this.onDragEnd(event);
    }
  }

  refreshActiveArea() {
    if (this.isMobileView) {
      (document.getElementsByClassName("activeArea")[0] as HTMLElement).style.height = `calc( ${this.sheetTransformYVal}dvh - var(--navbar-height))`;
      if (this.sheetTransformY == "10%") {
        return
      }
    } else {
      (document.getElementsByClassName("activeArea")[0] as HTMLElement).style.height = `100dvh`;
    }
    this.flyTo(this.currentMapFocus, undefined, { duration: 0.5 });
  }

  ////////////////////////////////////////////
  // UTILS
  ////////////////////////////////////////////
  formatDistance(meters?: number): string {
    if (!meters) return '--';
    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    } else {
      const kilometers = meters / 1000;
      return `${kilometers.toFixed(1)} km`;
    }
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3;
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c;
    return distance;
  }

  getDayIndices(): number[] {
    const locale = this.localeService.getLocale()

    if (['en-US'].includes(locale)) {
      return [0, 1, 2, 3, 4, 5, 6];
    }

    return [1, 2, 3, 4, 5, 6, 0];
  }

  getTodayNumber(): number {
    return new Date().getDay()
  }

  flyTo(latlon: LatLngExpression, zoom?: number, options?: ZoomPanOptions & { ignoreChecks?: boolean, tresholdDistance?: number }): void {
    let requested = latLng(latlon);
    if (requested.lat === 0 && requested.lng === 0) {
      return
    }

    if (this.map) {
      if (zoom === undefined) {
        zoom = this.map.getZoom() || this.DEFAULT_ZOOM;
      }
      if (this.isFirstPosition) {
        this.isFirstPosition = false;
        this.setView(latlon, zoom, options);
        return;
      }
      options = {
        ...options,
        duration: options?.duration ?? 0.4,
        tresholdDistance: options?.tresholdDistance === undefined ? 2500 : options.tresholdDistance,
      }

      let center = this.map.getCenter();

      if (zoom == this.map.getZoom() && options?.tresholdDistance && this.calculateDistance(center.lat, center.lng, requested.lat, requested.lng) < options.tresholdDistance) {
        this.setView(latlon, zoom, options);
        return;
      }
      if (!options?.ignoreChecks && center.lat === requested.lat && center.lng === requested.lng) {
        return;
      }
      this.map.flyTo(latlon, zoom, options);
      this.currentMapFocus = latlon;
    }
  }

  setView(latlon: LatLngExpression, zoom?: number, options?: ZoomPanOptions & { ignoreChecks?: boolean }): void {
    if (this.map) {
      options = {
        ...options,
        animate: options?.animate === undefined ? true : options.animate,
      }
      let center = this.map.getCenter();
      let requested = latLng(latlon);
      if (!options?.ignoreChecks && center.lat === requested.lat && center.lng === requested.lng) {
        return;
      }
      this.map.setView(requested, zoom, options);
      this.currentMapFocus = latlon;
    }
  }


  showStationsFilters() {
    this.stationsFiltersDialogVisible = true;
    this.tmpStationsFilters = structuredClone(this.stationsFilters);
  }

  hideStationsFilters() {
    this.stationsFiltersDialogVisible = false;
  }

  applyStationsFilters() {
    this.stationsFilters = structuredClone(this.tmpStationsFilters);
    this.searchRadiusCircle.setRadius(this.stationsFilters.searchRadius);
    this.refreshStations(undefined, true);
  }

  resetStationsFilters() {
    this.tmpStationsFilters = structuredClone(this.STATIONS_FILTERS_DEFAULT);
  }

  showArrivalStationsFilters() {
    this.arrivalStationsFiltersDialogVisible = true;
    this.tmpArrivalStationsFilters = structuredClone(this.arrivalStationsFilters);
  }

  hideArrivalStationsFilters() {
    this.arrivalStationsFiltersDialogVisible = false;
  }

  applyArrivalStationsFilters() {
    this.arrivalStationsFilters = structuredClone(this.tmpArrivalStationsFilters);
    this.searchRadiusCircle.setRadius(this.arrivalStationsFilters.searchRadius);
    this.refreshArrivalStations(undefined, true);
  }

  resetArrivalStationsFilters() {
    this.tmpArrivalStationsFilters = structuredClone(this.ARRIVAL_STATIONS_FILTERS_DEFAULT);
  }

  showChangeArrivalStationsFilters() {
    this.changeArrivalStationsFiltersDialogVisible = true;
    this.tmpChangeArrivalStationsFilters = structuredClone(this.changeArrivalStationsFilters);
  }

  hideChangeArrivalStationsFilters() {
    this.changeArrivalStationsFiltersDialogVisible = false;
  }

  applyChangeArrivalStationsFilters() {
    this.changeArrivalStationsFilters = structuredClone(this.tmpChangeArrivalStationsFilters);
    this.searchRadiusCircle.setRadius(this.changeArrivalStationsFilters.searchRadius);
    this.refreshChangeArrivalStations(undefined, true);
  }

  resetChangeArrivalStationsFilters() {
    this.tmpChangeArrivalStationsFilters = structuredClone(this.CHANGE_ARRIVAL_STATIONS_FILTERS_DEFAULT);
  }

  showAddressSearch() {
    this.mapAddressSearchDialogVisible = true;
  }

  hideAddressSearch() {
    this.mapAddressSearchDialogVisible = false;
  }

  onAddressSearch(evt: Event) {
    const query = (evt.target as HTMLInputElement).value
    this.mapAddressSearchSubject.next(query);
  }

  getPlaceIcon(place: any) {
    const type = place.addresstype || place.type;

    if (place.osm_type === 'way') {
      return "location_on";
    }

    switch (type) {
      // Administrative areas
      case 'city':
      case 'town':
        return "location_city";
      case 'village':
        return "cottage";
      case 'state':
      case 'province':
        return "map";
      case 'country':
        return "public";
      case 'neighbourhood':
        return "home_work";

      // Address components
      case 'street':
      case 'road':
        return "signpost";
      case 'house':
        return "home";

      // Transportation
      case 'airport':
        return "flight";
      case 'bus_stop':
      case 'bus_station':
        return "directions_bus";
      case 'railway_station':
        return "train";

      // Points of interest
      case 'hospital':
        return "local_hospital";
      case 'school':
      case 'university':
        return "school";
      case 'restaurant':
      case 'cafe':
        return "restaurant";
      case 'hotel':
        return "hotel";
      case 'park':
        return "park";
      case 'supermarket':
        return "shopping_cart";

      // Natural features
      case 'water':
      case 'river':
      case 'lake':
        return "water";
      case 'mountain':
      case 'peak':
        return "landscape";
      case 'forest':
        return "forest";
      case 'beach':
        return "beach_access";

      // Default for all other types
      default:
        return "location_on";
    }
  }

  onLocationSelected(location: any) {
    this.hideAddressSearch()
    this.setSearchLocationMarker(latLng(location.lat, location.lon));
  }

  onMapAddressSearchHide(input: HTMLInputElement) {
    this.mapAddressSearchResults = [];
    input.value = "";
    this.mapAddressSearchQuery = "";
    this.mapAddressSearchSubject.next("");
  }

  onMapAddressSearchShow(input: HTMLInputElement) {
    setTimeout(() => {
      input.focus()
    }, 50);
  }

  cardGroupAllowed(groupId: string): boolean {
    return this.cardGroups.find(g => g.id == this.activeGroupId)?.allowed?.includes(groupId) ?? true;
  }

  initCurRideIotDetails() {
    if (!("iotDetails" in this.currentRideData)) {
      this.currentRideData.iotDetails = {
        location: {},
        bike: {
          battery: {}
        },
        iot: {},
        lock: {}
      }
    }
  }
}
